// src/pages/UseCaseQADashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, Download, RefreshCw, Database,
  AlertCircle, ArrowLeft, Maximize2, Minimize2, Copy, FileJson, FileText
} from 'lucide-react';
import { buildApiUrl } from '@/config/appConfig';
import "./UseCaseQADashboard.css";

const UseCaseQADashboard = () => {
  const { useCaseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || 'all'; // Get from URL params, default to 'all'
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [summary, setSummary] = useState(null);
  const [allCollapsed, setAllCollapsed] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setData({});
    setSummary(null);

    try {
      let url = buildApiUrl(`/api/v2/qa/usecase/${useCaseId}`);
      if (clientId && clientId !== 'all') {
        url += `?clientId=${clientId}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const result = await response.json();
      
      // Extract data from 'tables' property
      const extractedData = {};
      if (result.tables) {
        for (const tableName in result.tables) {
          const tableData = result.tables[tableName];
          if (tableData && tableData.rows && tableData.rows.length > 0) {
            extractedData[tableName] = tableData.rows;
          }
        }
      }
      
      setData(extractedData);
      
      if (result.summary) {
        setSummary(result.summary);
      }
    } catch (err) {
      setError(`Failed to fetch data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [useCaseId, clientId]);

  // Derive available client IDs from the loaded data
  const availableClientIds = useMemo(() => {
    const clientIds = new Set();
    
    // Debug: Log the data structure
    console.log('QA Data for client derivation:', data);
    
    for (const [tableName, tableRows] of Object.entries(data || {})) {
      console.log(`Checking table: ${tableName}, rows:`, tableRows);
      (tableRows || []).forEach((row, index) => {
        if (row && typeof row.client_id !== 'undefined' && row.client_id !== null) {
          console.log(`Found client_id in ${tableName}[${index}]:`, row.client_id);
          clientIds.add(String(row.client_id)); // Keep as string to avoid conversion issues
        }
      });
    }
    
    console.log('All found client IDs:', [...clientIds]);
    
    const sortedClientIds = [...clientIds].sort((a, b) => {
      const numA = parseInt(a);
      const numB = parseInt(b);
      return numA - numB;
    });
    
    const options = [
      { label: 'All Clients', value: 'all' },
      ...sortedClientIds.map(id => ({ label: `Client ${id}`, value: id })),
      { label: 'Baseline (No Client)', value: '' }
    ];
    
    // Put selected client first, then others
    const selectedIndex = options.findIndex(opt => opt.value === clientId);
    if (selectedIndex > 0) {
      const selected = options.splice(selectedIndex, 1)[0];
      options.unshift(selected);
    }
    
    console.log('Final client options:', options);
    console.log('Current clientId:', clientId);
    
    return options;
  }, [data, clientId]);

  // Handle client selection change
  const handleClientChange = (newClientId) => {
    const params = new URLSearchParams(searchParams);
    if (newClientId && newClientId !== 'all') {
      params.set('clientId', newClientId);
    } else {
      params.delete('clientId');
    }
    navigate(`/qa/${useCaseId}?${params.toString()}`, { replace: true });
  };

  // Auto-fetch when component mounts or useCaseId changes
  useEffect(() => {
    if (useCaseId) {
      fetchData();
    }
  }, [useCaseId, fetchData]);

  const toggleSection = (tableName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  const toggleAllSections = () => {
    const newState = !allCollapsed;
    const newCollapsedState = {};
    Object.keys(data).forEach(tableName => {
      newCollapsedState[tableName] = newState;
    });
    setCollapsedSections(newCollapsedState);
    setAllCollapsed(newState);
  };

  const exportTableAsCSV = (tableName, tableData) => {
    if (!tableData || tableData.length === 0) return;
    
    const headers = Object.keys(tableData[0]);
    const csvContent = [
      headers.join(','),
      ...tableData.map(row => 
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tableName}_UC${useCaseId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportTableAsJSON = (tableName, tableData) => {
    if (!tableData || tableData.length === 0) return;
    
    const jsonContent = JSON.stringify(tableData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tableName}_UC${useCaseId}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const copyTableName = (tableName) => {
    navigator.clipboard.writeText(tableName);
    // Could add a toast notification here
  };

  const renderCellContent = (value) => {
    if (value === null || value === undefined) {
      return <span className="qa-null">—</span>;
    }
    
    if (typeof value === 'object') {
      return (
        <span className="qa-json-chip" title={JSON.stringify(value)}>
          {'{ }'} <span className="qa-json-preview">{Object.keys(value).length} fields</span>
        </span>
      );
    }
    
    const strValue = String(value);
    if (strValue.length > 100) {
      return (
        <span className="qa-truncated" title={strValue}>
          {strValue.substring(0, 100)}...
        </span>
      );
    }
    
    return strValue;
  };

  const renderTableData = (tableData) => {
    if (!tableData || tableData.length === 0) return null;

    const columns = Object.keys(tableData[0]);
    return (
      <div className="relative overflow-x-auto overflow-y-hidden max-w-full">
        <table className="min-w-max w-max qa-data-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => (
              <tr key={idx}>
                {columns.map(col => (
                  <td key={col}>
                    {renderCellContent(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const tableCategories = {
    'Profile & Customization': ['use_case_profiles', 'client_use_case_profile_sections'],
    'ROI & Financial': ['uc_roi_assumptions', 'uc_roi_calculations', 'uc_roi_sources', 'financial_projections'],
    'Implementation': ['uc_impl_milestones', 'uc_impl_requirements', 'uc_risks'],
    'Measures & Scoring': ['measure_rationalizations', 'client_measure_rationalizations', 'measure_results', 'client_use_case_scores'],
    'Configuration & Weights': ['client_category_weights', 'measure_weights', 'compliance_metadata', 'kpi_metrics']
  };

  return (
    <div className="qa-page">
      {/* Enhanced Sticky Header */}
      <div className="qa-sticky-header">
        <div className="qa-sticky-main">
          {/* Top Row - Use Case Info and Client Selector */}
          <div className="qa-header-top">
            <div className="qa-uc-info">
              <Link to="/qa/selector" className="qa-back-link">
                <ArrowLeft size={18} />
                <span>Back to Selector</span>
              </Link>
              <div className="qa-uc-badge">UC {useCaseId}</div>
              <div className="qa-uc-details">
                <h2>Use Case Quality Assurance</h2>
                <div className="qa-uc-category">Data Validation & Completeness Review</div>
              </div>
            </div>
            <div className="qa-client-selector">
              <div className="qa-client-label">
                <span>Client</span>
                {summary && <span className="qa-client-count">({summary.totalTables} tables)</span>}
              </div>
              <div className="qa-client-nav">
                <select
                  value={clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="qa-client-dropdown"
                  disabled={availableClientIds.length <= 1}
                  title="Show data rows for this client where available; otherwise Baseline rows."
                >
                  {availableClientIds.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Bottom Row - Stats and Actions */}
          <div className="qa-header-bottom">
            <div className="qa-stats-row">
              {summary && (
                <>
                  <div className="qa-stat-item">
                    <Database size={16} />
                    <div>
                      <div className="qa-stat-value">{summary.populatedTables}</div>
                      <div className="qa-stat-label">Tables</div>
                    </div>
                  </div>
                  <span className="qa-stat-divider">•</span>
                  <div className="qa-stat-item">
                    <div>
                      <div className="qa-stat-value">{summary.totalRows}</div>
                      <div className="qa-stat-label">Total Rows</div>
                    </div>
                  </div>
                  <span className="qa-stat-divider">•</span>
                  <div className="qa-stat-item">
                    <div>
                      <div className="qa-stat-value">{Math.round((summary.populatedTables / summary.totalTables) * 100)}%</div>
                      <div className="qa-stat-label">Complete</div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="qa-action-buttons">
              <button onClick={toggleAllSections} className="qa-action-btn qa-expand-btn">
                {allCollapsed ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                {allCollapsed ? 'Expand All' : 'Collapse All'}
              </button>
              <button onClick={fetchData} disabled={loading} className="qa-action-btn qa-reload-btn">
                <RefreshCw size={16} className={loading ? 'qa-spin' : ''} />
                Reload
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="qa-main-content">

        {error && (
          <div className="qa-error-card">
            <div className="qa-error-icon">
              <AlertCircle size={20} />
            </div>
            <div className="qa-error-content">
              <h4>Failed to load data</h4>
              <p>{error}</p>
            </div>
          </div>
        )}

        {Object.keys(data).length > 0 && (
          <div className="qa-tables">
            {Object.entries(tableCategories).map(([category, tables]) => {
              const categoryTables = tables.filter(table => data[table]);
              if (categoryTables.length === 0) return null;

              return (
                <div key={category} className="qa-category">
                  <h2 className="qa-category-title">{category}</h2>
                  {categoryTables.map(tableName => (
                    <div key={tableName} className="qa-table-section">
                      <div className="qa-table-header" onClick={() => toggleSection(tableName)}>
                        <div className="qa-table-title">
                          <div className="qa-chevron-icon">
                            {collapsedSections[tableName] ? <ChevronDown size={24} /> : <ChevronUp size={24} />}
                          </div>
                          <div className="qa-table-info">
                            <h3>{tableName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                            <span className="qa-table-subtitle">{tableName}</span>
                          </div>
                          <span className="qa-record-badge">
                            {data[tableName]?.length || 0} records
                          </span>
                        </div>
                        <div className="qa-table-actions" onClick={(e) => e.stopPropagation()}>
                          <button 
                            onClick={() => exportTableAsCSV(tableName, data[tableName])}
                            className="qa-icon-btn qa-export-btn"
                            title="Export as CSV"
                          >
                            <FileText size={16} />
                          </button>
                          <button 
                            onClick={() => exportTableAsJSON(tableName, data[tableName])}
                            className="qa-icon-btn qa-export-btn"
                            title="Export as JSON"
                          >
                            <FileJson size={16} />
                          </button>
                          <button 
                            onClick={() => copyTableName(tableName)}
                            className="qa-icon-btn"
                            title="Copy table name"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      {!collapsedSections[tableName] && (
                        <div className="qa-table-content qa-slide-down">
                          {data[tableName].length === 0 ? (
                            <div className="qa-empty-state-card">
                              <Database size={32} className="qa-empty-icon" />
                              <p>No data available</p>
                              <span className="qa-empty-subtitle">This table contains no records for the current context</span>
                            </div>
                          ) : (
                            renderTableData(data[tableName])
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {!loading && Object.keys(data).length === 0 && !error && (
          <div className="qa-empty-state-main">
            <Database size={64} className="qa-empty-icon" />
            <h3>No data available</h3>
            <p>Click Reload to fetch data or verify your Client ID is correct.</p>
            <button onClick={fetchData} className="qa-primary-btn">
              <RefreshCw size={16} />
              Reload Data
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UseCaseQADashboard;